/**
 * Drone D1 #319 + #321 — gov/requestor missions panel (COP side). Lists the agency's drone
 * missions and drives the requestor-side transitions: confirm (accepted → confirmed, ratifying
 * the operator's accept-terms), cancel (pre-flown), mark billed (flown → billed), mark paid
 * (billed → paid). The available actions come straight from the shared state machine
 * (availableMissionActions), so the UI never offers an illegal edge — the server re-validates
 * regardless. The SERVER scribes each transition, so this client does NOT logAction (no double
 * audit). The operator's dispatch terms (ETA / quoted price / feed kind) are surfaced so the
 * requestor confirms against them. Reachable from the Drone Operator Coverage panel.
 */
import { useEffect, useState } from 'react';
import { availableMissionActions } from '@tsr/shared-types';
import {
  billDroneMission,
  cancelDroneMission,
  confirmDroneMission,
  fetchDroneMissions,
  payDroneMission,
  type DroneMissionView,
} from './api';
import {
  capabilitiesSummary,
  isOpenMission,
  missionBillingLabel,
  missionStatusBadge,
  missionTermsSummary,
} from './droneMissionUi';
import { MissionProgress } from './MissionProgress';

type GovAction = 'confirm' | 'cancel' | 'bill' | 'pay';

const ACTION_LABEL: Record<GovAction, string> = {
  confirm: 'Confirm',
  cancel: 'Cancel',
  bill: 'Mark billed',
  pay: 'Mark paid',
};
const ACTION_CLS: Record<GovAction, string> = {
  confirm: 'bg-indigo-600 hover:bg-indigo-500 text-white',
  cancel: 'border border-slate-500/50 text-slate-300 hover:bg-slate-700/50',
  bill: 'bg-sky-600 hover:bg-sky-500 text-white',
  pay: 'bg-emerald-600 hover:bg-emerald-500 text-white',
};

// Looked up lazily inside the click handler (NOT a module-level const) so importing this
// module never touches the `./api` exports — a partial `vi.mock('./api')` in an unrelated
// test that transitively loads this panel would otherwise throw at module-eval.
function runGovAction(action: GovAction, id: string): Promise<DroneMissionView> {
  if (action === 'confirm') return confirmDroneMission(id);
  if (action === 'cancel') return cancelDroneMission(id);
  if (action === 'bill') return billDroneMission(id);
  return payDroneMission(id);
}

export function DroneMissionsPanel({
  refreshKey,
  onSelectMission,
  onActiveCountChange,
}: {
  refreshKey: number;
  /** #322 — open a mission's lifecycle + live-feed detail panel (row header click). */
  onSelectMission?: (m: DroneMissionView) => void;
  /** #374 — report the count of OPEN (non-terminal) missions after each load, so a host
   *  surface (the LeftRail "Requests" tab) can badge how many requests are still in flight. */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [missions, setMissions] = useState<DroneMissionView[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function reload(): Promise<void> {
    try {
      setMissions(await fetchDroneMissions());
    } catch {
      /* keep current */
    }
  }
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Report the OPEN (in-flight) count up from a single pure effect — covers BOTH the fetch
  // (reload) and the optimistic transition (act) paths without a side effect inside a state
  // updater, and re-fires only when the mission set actually changes.
  useEffect(() => {
    onActiveCountChange?.(missions.filter((m) => isOpenMission(m.status)).length);
  }, [missions, onActiveCountChange]);

  async function act(id: string, action: GovAction): Promise<void> {
    setBusyId(id);
    try {
      const updated = await runGovAction(action, id);
      setMissions((prev) => prev.map((m) => (m.id === id ? updated : m)));
    } catch {
      void reload();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="border-t border-slate-800 pt-3" data-testid="drone-missions-panel">
      <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Your drone missions</h3>
      {missions.length === 0 ? (
        <p className="text-[11px] text-slate-500">
          No drone missions yet. Pick an operator from “Request live eyes” to raise one.
        </p>
      ) : (
        <ul className="space-y-2">
          {missions.map((m) => {
            const badge = missionStatusBadge(m.status);
            const actions = availableMissionActions(m.status, 'requestor') as GovAction[];
            const terms = missionTermsSummary(m);
            return (
              <li
                key={m.id}
                className="rounded border border-slate-700/60 bg-slate-800/40 px-3 py-2"
                data-testid="drone-mission-row"
              >
                <div className="flex items-start justify-between gap-2">
                  {onSelectMission ? (
                    <button
                      type="button"
                      onClick={() => onSelectMission(m)}
                      data-testid="mission-open"
                      className="min-w-0 text-left rounded -mx-1 px-1 hover:bg-slate-700/40"
                      title="Open mission lifecycle + live feed"
                    >
                      <p className="text-sm font-medium truncate">{m.operator_name ?? 'Operator'}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {m.asset_name ?? 'Asset TBD'} · {capabilitiesSummary(m.requested_capabilities)}
                      </p>
                    </button>
                  ) : (
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{m.operator_name ?? 'Operator'}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {m.asset_name ?? 'Asset TBD'} · {capabilitiesSummary(m.requested_capabilities)}
                      </p>
                    </div>
                  )}
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}>
                    {badge.label}
                  </span>
                </div>
                {/* #374 — at-a-glance progress (requested → on station); hidden for terminal off-ramps. */}
                <MissionProgress status={m.status} />
                {terms && (
                  <p className="mt-1 text-[11px] text-slate-300" data-testid="mission-terms">
                    {terms}
                  </p>
                )}
                {/* Situation-Unit feed click-through (§I.2): a live feed is no longer a dead-end
                    line — when the mission can be opened, the indicator is a button that opens the
                    {kind:'mission'} detail where MissionFeedEmbed plays the feed. Falls back to the
                    static note when there's no open-mission handler. */}
                {m.feed_url &&
                  (onSelectMission ? (
                    <button
                      type="button"
                      onClick={() => onSelectMission(m)}
                      data-testid="mission-feed-open"
                      className="mt-1 block text-left text-[11px] text-cyan-300 hover:text-cyan-200 hover:underline"
                      title="Open the mission to watch the live feed"
                    >
                      ▶ Open live feed
                    </button>
                  ) : (
                    <p className="mt-1 text-[11px] text-slate-400" data-testid="mission-feed-note">
                      Feed link provided
                    </p>
                  ))}
                {m.declined_reason && (
                  <p className="mt-1 text-[11px] text-slate-400">Declined: {m.declined_reason}</p>
                )}
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500">
                    {m.billable ? 'Billable' : 'No charge'}
                    {m.ledger ? ` · ${missionBillingLabel(m.ledger.status)}` : ''}
                  </span>
                  {actions.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      {actions.map((a) => (
                        <button
                          key={a}
                          type="button"
                          disabled={busyId === m.id}
                          onClick={() => void act(m.id, a)}
                          className={`rounded px-2 py-0.5 text-[11px] font-medium disabled:opacity-50 ${ACTION_CLS[a]}`}
                        >
                          {ACTION_LABEL[a]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
